// The project's database-design workspace (plan feature 6, the end goal of the portal):
// candidate tables from every described tabular file, cross-study key proposals to review,
// shared entities, the ER diagram, statistics/data quality, the data check (DataCheckPanel:
// bad rows of every table before a build; its errors block Build), and Build into proj_<slug>.
//
// Data: GET /api/projects/{id}/dbdesign returns the whole workspace; every change answers
// with the updated workspace, which replaces the local copy. Backend: unio/routers/dbdesign.py.
// Ported database-creator pieces live next to this file (SchemaDiagram, erDiagram,
// StatsPanel, DataQualityPanel); the ER diagram is lazy-loaded so mermaid (large, ESM-only)
// stays out of the main bundle and out of tests that never open this page.
import { lazy, Suspense, useCallback, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../../api";
import AppLayout from "../../components/AppLayout";
import { ErrorNote, Loading, SectionTitle } from "../../components/Feedback";
import { Btn, Card, NeedsLargerScreen } from "../../components/Shared";
import { focusWhenReady, rememberFocus } from "../../focus";
import { useApi } from "../../useApi";
import BuildPanel from "./BuildPanel";
import DataCheckPanel from "./DataCheckPanel";
import DataQualityPanel from "./DataQualityPanel";
import { designToDiagram } from "./designDiagram";
import EntitiesPanel from "./EntitiesPanel";
import KeyReview from "./KeyReview";
import LinkmlPanel from "./LinkmlPanel";
import StatsPanel from "./StatsPanel";
import TablesPanel from "./TablesPanel";

const SchemaDiagram = lazy(() => import("./SchemaDiagram"));

const section = { marginBottom: 24, padding: "22px 26px" };

// The live region is always mounted and only its text changes: screen readers often skip a
// role=status element that is inserted together with its message (review of the a11y pass).
function Notice({ notice }) {
  const shown = notice
    ? { padding: "10px 14px", borderRadius: 8, background: "var(--dp-accent-bg)", border: "1px solid rgba(0,150,64,0.3)", color: "var(--dp-accent-dark)", fontSize: 13, margin: "0 0 14px" }
    : {};
  return (
    <div role="status" aria-live="polite" data-testid="design-notice" style={shown}>
      {notice || ""}
    </div>
  );
}

function Reports({ projectId }) {
  const [stats, setStats] = useState(null);
  const [quality, setQuality] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const load = async (what, setter) => {
    setBusy(what);
    setError(null);
    try { setter(await api.get(`/projects/${projectId}/dbdesign/${what}`)); } catch (e) { setError(e); } finally { setBusy(null); }
  };
  return (
    <div>
      <ErrorNote error={error} />
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
        <Btn variant="ghost" disabled={!!busy} onClick={() => load("stats", setStats)}>
          {busy === "stats" ? "Loading statistics…" : stats ? "Refresh statistics" : "Show statistics"}
        </Btn>
        <Btn variant="ghost" disabled={!!busy} onClick={() => load("data-quality", setQuality)}>
          {busy === "data-quality" ? "Checking data quality…" : quality ? "Refresh data quality" : "Show data quality"}
        </Btn>
      </div>
      {stats && <div style={{ marginBottom: 20 }}><StatsPanel stats={stats} /></div>}
      {quality && <DataQualityPanel report={quality} />}
    </div>
  );
}

export default function DbDesignPage() {
  const { projectId } = useParams();
  const project = useApi(`/projects/${projectId}`);
  const loaded = useApi(`/projects/${projectId}/dbdesign`);
  const versionsApi = useApi(`/projects/${projectId}/dbdesign/versions`);
  const [local, setLocal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const p = project.data;
  const ws = local || loaded.data;
  const canManage = !!ws?.can_manage;
  const spec = useMemo(() => (ws ? designToDiagram(ws.design) : null), [ws]);
  const { reload: reloadWs } = loaded;
  const { reload: reloadVersions } = versionsApi;
  const keysRef = useRef(null);
  const buildRef = useRef(null);
  const tablesRef = useRef(null);
  const entitiesRef = useRef(null);
  const inside = (ref, selector) => () => ref.current && ref.current.querySelector(selector);

  // Run a change; the server answers with the new workspace. Every button is disabled while it
  // runs, which drops keyboard focus to <body>: it is given back to the button that was pressed,
  // or, when that button is gone (Accept on an accepted link), to `fallback()`.
  // `onError(e)`, when given, also hands the error to the form that sent it.
  const run = useCallback(async (fn, done, fallback, onError) => {
    const restore = rememberFocus();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const next = await fn();
      setLocal(next);
      if (done) setNotice(done(next));
      return true;
    } catch (e) {
      setError(e);
      if (onError) onError(e);
      return false;
    } finally {
      setBusy(false);
      restore(fallback);
    }
  }, []);

  const base = `/projects/${projectId}/dbdesign`;
  const findLinks = () => run(() => api.post(`${base}/proposals`),
    next => `Compared ${next.summary.tables} tables: ${next.summary.proposed} new proposal${next.summary.proposed === 1 ? "" : "s"}.`
      + (next.summary.entity_proposed ? ` ${next.summary.entity_proposed} proposed member${next.summary.entity_proposed === 1 ? "" : "s"} of shared entities.` : ""));
  const firstKeyButton = () => keysRef.current && keysRef.current.querySelector("button:not([disabled])");
  // accept | reject | reset (KeyReview's LinkRow), each named by what it did.
  const MAPPING_DONE = { accept: "accepted", reject: "rejected", reset: "reset to proposed" };
  const mappingAction = (m, action) => run(() => api.post(`${base}/mappings/${m.id}/${action}`),
    () => `${m.subject.table}.${m.subject.field} → ${m.object.table}.${m.object.field} ${MAPPING_DONE[action]}.`,
    () => (keysRef.current && keysRef.current.querySelector(`[data-mapping="${m.id}"] [data-actions] button`)) || firstKeyButton());
  const SPACE_DONE = { same: "confirmed as the same identifier space", different: "confirmed as different identifier spaces" };
  const mappingSpace = (m, space) => run(
    () => api.put(`${base}/mappings/${m.id}/identifier-space`, { identifier_space: space }),
    () => `${m.subject.table}.${m.subject.field} → ${m.object.table}.${m.object.field} ${SPACE_DONE[space] || "no longer confirmed"}.`,
    () => (keysRef.current && keysRef.current.querySelector(`[data-mapping="${m.id}"] [data-space] button`)) || firstKeyButton());
  const entitySpace = (e, space, namespacing) => run(
    () => api.put(`${base}/entities/${e.id}/identifier-space`, { identifier_space: space, namespacing }),
    () => `Shared entity ${e.name} ${SPACE_DONE[space] || "no longer confirmed"}.`,
    inside(entitiesRef, "button:not([disabled]), select"));
  const addLink = (s, o) => run(() => api.post(`${base}/mappings`, { subject_column_id: s, object_column_id: o }),
    () => "Link checked against the data and added.",
    inside(keysRef, "form select"));
  const saveTables = tables => run(() => api.put(base, { tables }), () => "Tables saved.", inside(tablesRef, "input"));
  // Hand-defined tables and their links (HandTablesPanel): method "post" | "put" | "del" under .../dbdesign;
  // `onError` shows a 422 at the column it names.
  const handTables = (method, path, body, done, onError) => run(() => api[method](`${base}${path}`, body), () => done,
    inside(tablesRef, "button:not([disabled]), input"), onError);
  const createEntity = body => run(() => api.post(`${base}/entities`, body), () => `Shared entity ${body.name} created.`,
    inside(entitiesRef, "form input"));
  // A proposed member of a shared entity (a same_entity mapping from Find key links): accept / reject.
  const memberAction = (e, p, action) => run(() => api.post(`${base}/mappings/${p.mapping_id}/${action}`),
    () => `${p.table}.${p.field} ${action === "accept" ? "accepted as a member of" : "rejected as a member of"} ${e.name}.`,
    inside(entitiesRef, "button:not([disabled])"));
  // A LinkML schema whose classes become shared entities (LinkmlPanel; routers/dbdesign_linkml.py).
  const importLinkml = text => run(() => api.post(`${base}/linkml`, { schema: text }),
    next => {
      const n = next.imported.created.length;
      return `Imported ${n} shared entit${n === 1 ? "y" : "ies"} from ${next.imported.schema.name || "the schema"}.`;
    },
    inside(entitiesRef, "button:not([disabled]), input"));
  const deleteEntity = e => run(() => api.del(`${base}/entities/${e.id}`), () => `Shared entity ${e.name} removed.`,
    inside(entitiesRef, "button, input"));
  // Called when a build starts (202) and again when it ends: the Build button is disabled while
  // the build runs, so focus goes back to it once it is enabled again (if focus fell to <body>).
  const onBuildChange = () => {
    setLocal(null); reloadWs(); reloadVersions();
    focusWhenReady(() => buildRef.current && buildRef.current.querySelector("button"));
  };

  return (
    <AppLayout
      title="Database design"
      crumbs={[{ label: "Projects", to: "/projects" },
               { label: p ? p.name : "…", to: `/projects/${projectId}` },
               { label: "Database design" }]}
    >
      <ErrorNote error={loaded.error} />
      {loaded.loading && !ws && <Loading />}
      {ws && (
        <>
          <p style={{ margin: "0 0 18px", fontSize: 14, color: "#374151", maxWidth: 820 }}>
            Every described tabular file of the project is a candidate table. Review the proposed key
            links between the studies' tables, merge shared columns into entities, then build the shared
            project database.{!canManage && " Only a curator of the project can change the design."}
          </p>
          <ErrorNote error={error} />
          <Notice notice={notice} />

          <Card style={section}>
            <SectionTitle>Build</SectionTitle>
            <div ref={buildRef}>
              <BuildPanel ws={ws} versions={versionsApi.data || []} canManage={canManage} onChange={onBuildChange} />
            </div>
          </Card>

          <Card style={section}>
            <SectionTitle>Data check</SectionTitle>
            <DataCheckPanel projectId={projectId} slug={p?.slug} />
          </Card>

          {/* Key links, tables, entities, the diagram and the reports are wide editors: below
              768 px they say so and offer "Show it anyway" (Shared.jsx NeedsLargerScreen); Build
              and the data check above stay, laid out for a phone. */}
          <NeedsLargerScreen what="The design editor (key links, tables, shared entities, the diagram)">
          <Card style={section}>
            <SectionTitle right={canManage && (
              <Btn onClick={findLinks} disabled={busy || ws.tables.length < 2}>Find key links</Btn>
            )}>
              Key links
            </SectionTitle>
            {ws.proposals_computed_at && (
              <p style={{ margin: "-6px 0 10px", fontSize: 12, color: "#6b7280" }}>
                Last compared {new Date(ws.proposals_computed_at).toLocaleString()}.
              </p>
            )}
            <div ref={keysRef}>
              <KeyReview ws={ws} canManage={canManage} busy={busy} onAction={mappingAction} onAdd={addLink}
                         onSpace={mappingSpace} />
            </div>
          </Card>

          <Card style={section}>
            <SectionTitle>Tables</SectionTitle>
            <div ref={tablesRef}>
              <TablesPanel tables={ws.tables} canManage={canManage} busy={busy} onSave={saveTables}
                           ws={ws} onHand={handTables} />
            </div>
          </Card>

          <Card style={section}>
            <SectionTitle>Shared entities</SectionTitle>
            <div ref={entitiesRef}>
              <EntitiesPanel ws={ws} canManage={canManage} busy={busy} onCreate={createEntity} onDelete={deleteEntity}
                             onSpace={entitySpace} onMember={memberAction} />
              <LinkmlPanel projectId={projectId} slug={p?.slug} canManage={canManage} busy={busy}
                           imported={ws.imported} onImport={importLinkml} />
            </div>
          </Card>

          <Card style={section}>
            <SectionTitle>ER diagram</SectionTitle>
            {ws.design.tables.length === 0 ? (
              <p style={{ fontSize: 13, color: "#6b7280", margin: 0 }}>No tables to draw.</p>
            ) : (
              <figure style={{ margin: 0 }}>
                {/* The diagram's text alternative: the same tables and links, as lists, are above. */}
                <figcaption style={{ fontSize: 13, color: "#4b5563", margin: "0 0 10px" }}>
                  A picture of the design: each box is a table or a shared entity, each line an accepted
                  key link or an entity membership. The Tables, Key links and Shared entities sections
                  above list the same in text.
                </figcaption>
                <Suspense fallback={<Loading label="Drawing the diagram…" />}>
                  <SchemaDiagram spec={spec} />
                </Suspense>
              </figure>
            )}
          </Card>

          <Card style={section}>
            <SectionTitle>Statistics and data quality</SectionTitle>
            <Reports projectId={projectId} />
          </Card>
          </NeedsLargerScreen>
        </>
      )}
    </AppLayout>
  );
}
